import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SideMenu } from '@shore/react-gravity';
import { useDispatch } from 'react-redux';
import { changeLocation } from '../../../actions';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import EVENT_BUS_EVENTS from '../../../constants/event-bus-events';
import { useNavigationPostMessage } from '@shared/hooks/useNavigationPostMessage';
import { BookingsUpgradeSidebarCardContainer } from '../../../features/bookings-upgrade/containers';

const SIDE_MENU_CLICK_EVENT = 'side_menu_item_clicked';

const MENU_ROUTES = {
  calendar: '/calendar',
  servicesV2: '/services-v2',
  settingsV2Booking: '/settings-v2/booking',
  settingsV2Email: '/settings-v2/email',
  settingsV2Company: '/settings-v2/company',
} as const;

const MENU_ROUTE_VALUES = Object.values(MENU_ROUTES);

export function FlatSideMenu() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { trackMethod, eventBus } = useGlobals();
  const { sendNavigationMessage } = useNavigationPostMessage();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const isPathActive = (paths: string[]) => {
    return paths.some((path) => {
      if (path === '/') {
        return location.pathname === '/' || location.pathname === '';
      }
      return location.pathname.startsWith(path);
    });
  };

  const handleNavigation = (path: string) => {
    // navigate(path);
    dispatch(changeLocation(path));
    trackMethod(SIDE_MENU_CLICK_EVENT, {
      platform: 'booking',
      location: 'side_menu_link',
      menu_item: path.replace(/#\/|\//g, ''),
    });
    eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, { payload: { route: path } });
  };

  useEffect(() => {
    if (
      MENU_ROUTE_VALUES.some((route) =>
        // Calendar uses strict equality because its micro-app does hash routing internally.
        // startsWith would match e.g. /calendar#/some-hash, bubbling those internal
        // transitions up to the parent Aida app and breaking merchant navigation.
        route === MENU_ROUTES.calendar
          ? location.pathname === route
          : location.pathname.startsWith(route)
      )
    ) {
      sendNavigationMessage(location.pathname);
    }
  }, [location.pathname, sendNavigationMessage]);

  const isSettingsExpanded = useMemo(() => {
    const isSettingsActive = isPathActive([
      '/settings',
      '/application-settings',
    ]);
    return isSettingsActive || expandedItems.includes('settings');
  }, [location.pathname, expandedItems]);

  const toggleSubmenuExpansion = (itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  return (
    <div className="gv-sidebar gv-flex-column-sm gv-h-full gv-max-tab-hidden">
      <SideMenu
        title="Bookings"
        items={[
          {
            id: 'calendar',
            label: 'Calendar',
            isActive: isPathActive([MENU_ROUTES.calendar]),
            as: 'button' as const,
            onClick: () => handleNavigation(MENU_ROUTES.calendar),
          },
          {
            id: 'services',
            label: 'Services',
            isActive: isPathActive([MENU_ROUTES.servicesV2]),
            as: 'button' as const,
            onClick: () => handleNavigation(MENU_ROUTES.servicesV2),
          },
          {
            id: 'settings-v2',
            label: 'Settings',
            isActive: isPathActive(['/settings-v2']),
            as: 'button' as const,
            onClick: () => {
              if (isSettingsExpanded) {
                toggleSubmenuExpansion('settings-v2');
              } else {
                handleNavigation(MENU_ROUTES.settingsV2Booking);
                setExpandedItems((prev) => [...prev, 'settings-v2']);
              }
            },
            isExpanded: isSettingsExpanded,
            submenu: [
              {
                id: 'booking_settings',
                label: 'Booking settings',
                isActive: isPathActive([MENU_ROUTES.settingsV2Booking]),
                as: 'button' as const,
                onClick: () => handleNavigation(MENU_ROUTES.settingsV2Booking),
              },
              {
                id: 'email_settings',
                label: 'Email settings',
                isActive: isPathActive([MENU_ROUTES.settingsV2Email]),
                as: 'button' as const,
                onClick: () => handleNavigation(MENU_ROUTES.settingsV2Email),
              },
              {
                id: 'company_settings',
                label: 'Company settings',
                isActive: isPathActive([MENU_ROUTES.settingsV2Company]),
                as: 'button' as const,
                onClick: () => handleNavigation(MENU_ROUTES.settingsV2Company),
              },
            ],
          },
        ]}
      />
      <BookingsUpgradeSidebarCardContainer />
    </div>
  );
}
